/**
 * The transparent drawing surface for Review Mode.
 *
 * Critical property: it intercepts pointer events ONLY while a tool is
 * selected. With no tool active it is `pointer-events: none`, so Review Mode
 * being on never stops anyone clicking through the app — reviewers navigate
 * normally and only "pick up a pen" when they want to mark something.
 *
 * Marked `data-review-overlay` so `captureScreenshot` excludes it — the
 * stored image is the clean screen, and shapes replay over it as vectors.
 *
 * Port fixes vs the reference implementation:
 *   - pointer capture on `e.currentTarget` (the overlay), not `e.target`;
 *   - viewport size tracked via a resize listener, so a mid-session window
 *     resize can't leave a stale coordinate space;
 *   - the text tool uses an inline input (committing on Enter or outside
 *     click) instead of `window.prompt`, which some PWA contexts block;
 *   - a `redact` tool (rect geometry, solid fill).
 */
import React from 'react';
import type { ReviewShape, ReviewTool } from '../../types/screenReview.types';
import { ShapeRenderer } from './ShapeRenderer';

interface Props {
  tool: ReviewTool | null;
  shapes: ReviewShape[];
  onAddShape: (shape: ReviewShape) => void;
}

/** Ignore freehand points closer than this (fraction of viewport) — keeps polylines small. */
const MIN_POINT_GAP = 0.004;

interface TextDraft {
  x: number;
  y: number;
  value: string;
}

export const AnnotationOverlay: React.FC<Props> = ({ tool, shapes, onAddShape }) => {
  const [draft, setDraft] = React.useState<ReviewShape | null>(null);
  const [textDraft, setTextDraft] = React.useState<TextDraft | null>(null);
  const [viewport, setViewport] = React.useState({
    w: window.innerWidth,
    h: window.innerHeight,
  });
  const dragging = React.useRef(false);
  const textInputRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    const onResize = (): void =>
      setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Switching tools abandons an uncommitted text label.
  React.useEffect(() => {
    if (tool !== 'text') setTextDraft(null);
  }, [tool]);

  const toPct = (e: React.PointerEvent): { x: number; y: number } => ({
    x: Math.min(1, Math.max(0, e.clientX / window.innerWidth)),
    y: Math.min(1, Math.max(0, e.clientY / window.innerHeight)),
  });

  const commitTextDraft = (): void => {
    if (!textDraft) return;
    const text = textDraft.value.trim();
    if (text) onAddShape({ kind: 'text', x: textDraft.x, y: textDraft.y, text });
    setTextDraft(null);
  };

  const onPointerDown = (e: React.PointerEvent): void => {
    if (!tool) return;
    const { x, y } = toPct(e);

    if (textDraft) {
      // A click outside the input commits the pending label instead of
      // starting a new shape.
      commitTextDraft();
      return;
    }

    if (tool === 'pin') {
      onAddShape({ kind: 'pin', x, y });
      return;
    }
    if (tool === 'text') {
      setTextDraft({ x, y, value: '' });
      window.setTimeout(() => textInputRef.current?.focus(), 0);
      return;
    }

    dragging.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    if (tool === 'arrow') setDraft({ kind: 'arrow', x1: x, y1: y, x2: x, y2: y });
    if (tool === 'rect') setDraft({ kind: 'rect', x, y, w: 0, h: 0 });
    if (tool === 'redact') setDraft({ kind: 'redact', x, y, w: 0, h: 0 });
    if (tool === 'freehand') setDraft({ kind: 'freehand', points: [[x, y]] });
  };

  const onPointerMove = (e: React.PointerEvent): void => {
    if (!dragging.current || !draft) return;
    const { x, y } = toPct(e);
    if (draft.kind === 'arrow') setDraft({ ...draft, x2: x, y2: y });
    if (draft.kind === 'rect' || draft.kind === 'redact') {
      setDraft({ ...draft, w: x - draft.x, h: y - draft.y });
    }
    if (draft.kind === 'freehand') {
      const [lx, ly] = draft.points[draft.points.length - 1];
      if (Math.hypot(x - lx, y - ly) >= MIN_POINT_GAP) {
        setDraft({ ...draft, points: [...draft.points, [x, y]] });
      }
    }
  };

  const onPointerUp = (): void => {
    if (!dragging.current) return;
    dragging.current = false;
    if (!draft) return;

    // Normalise a rect dragged up/left, and drop degenerate shapes — a
    // zero-size box or two-point scribble is a slip, not an annotation.
    let final: ReviewShape | null = draft;
    if (draft.kind === 'rect' || draft.kind === 'redact') {
      const x = Math.min(draft.x, draft.x + draft.w);
      const y = Math.min(draft.y, draft.y + draft.h);
      const w = Math.abs(draft.w);
      const h = Math.abs(draft.h);
      final = w > 0.005 && h > 0.005 ? { kind: draft.kind, x, y, w, h } : null;
    }
    if (draft.kind === 'arrow') {
      const len = Math.hypot(draft.x2 - draft.x1, draft.y2 - draft.y1);
      if (len < 0.01) final = null;
    }
    if (draft.kind === 'freehand' && draft.points.length < 3) final = null;

    setDraft(null);
    if (final) onAddShape(final);
  };

  return (
    <div
      data-review-overlay=""
      className="fixed inset-0 z-[10305]"
      style={{
        pointerEvents: tool ? 'auto' : 'none',
        cursor: tool ? 'crosshair' : undefined,
        touchAction: tool ? 'none' : undefined,
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <ShapeRenderer
        shapes={draft ? [...shapes, draft] : shapes}
        width={viewport.w}
        height={viewport.h}
      />
      {textDraft && (
        <input
          ref={textInputRef}
          type="text"
          value={textDraft.value}
          onChange={(e) => setTextDraft({ ...textDraft, value: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitTextDraft();
            if (e.key === 'Escape') setTextDraft(null);
          }}
          onBlur={commitTextDraft}
          onPointerDown={(e) => e.stopPropagation()}
          placeholder="Label…"
          className="absolute rounded border-2 border-rose-600 bg-white px-2 py-1 text-sm font-bold text-rose-600 shadow-lg outline-none"
          style={{
            left: `${textDraft.x * 100}%`,
            top: `${textDraft.y * 100}%`,
            transform: 'translateY(-100%)',
            pointerEvents: 'auto',
            maxWidth: '40vw',
          }}
        />
      )}
    </div>
  );
};

export default AnnotationOverlay;
